import { Directive, ElementRef, EventEmitter, HostListener, Input, Output } from "@angular/core";

/**
 * Gives a hand-rolled modal overlay the two dismissal gestures users already get
 * from the shared `app-modal`: pressing Escape, and clicking the backdrop.
 *
 * Several screens render their own overlay markup instead of `app-modal` (the
 * webhook payload previews, the preference page render options). Those overlays
 * cover the page and swallow pointer events, so a user who does not spot the
 * small close control reads the screen as frozen. Apply this directive to the
 * backdrop element so those overlays behave like every other dialog.
 *
 * Deliberately emits an event instead of hiding anything itself: the host owns
 * the flag that renders the overlay, and dismissal must never be able to trigger
 * a confirm action.
 */
@Directive({
  selector: "[dirDismissableOverlay]",
  standalone: true
})
export class DismissableOverlayDirective {
  /** Set to false for overlays where a stray backdrop click would lose work. */
  @Input() public dismissOnBackdropClick: boolean = true;

  @Output() public dismissed: EventEmitter<void> = new EventEmitter<void>();

  /**
   * A click whose mousedown began inside the dialog is a drag-select that ended
   * on the backdrop, not a dismissal. Closing there would throw away a payload
   * the user was in the middle of selecting to copy.
   */
  private pressStartedOnBackdrop: boolean = false;

  public constructor(private readonly elementRef: ElementRef<HTMLElement>) {}

  @HostListener("document:keydown.escape")
  public onEscapeKey(): void {
    this.dismissed.emit();
  }

  @HostListener("mousedown", ["$event"])
  public onBackdropMouseDown(event: MouseEvent): void {
    this.pressStartedOnBackdrop = this.isBackdrop(event.target);
  }

  @HostListener("click", ["$event"])
  public onBackdropClick(event: MouseEvent): void {
    const dismissedByBackdrop: boolean = this.pressStartedOnBackdrop && this.isBackdrop(event.target);
    this.pressStartedOnBackdrop = false;

    if (this.dismissOnBackdropClick && dismissedByBackdrop) {
      this.dismissed.emit();
    }
  }

  private isBackdrop(target: EventTarget | undefined): boolean {
    return target === this.elementRef.nativeElement;
  }
}
